/**
 * Single source of truth for SEO/GEO site-level constants for the microsite.
 *
 * SITE_URL is baked at build time from VITE_SITE_URL (set in .env.production).
 * This matters for build-time prerendering: puppeteer renders the built bundle
 * from a localhost preview server, but VITE_SITE_URL is already inlined, so
 * canonical/og:url/sitemap URLs resolve to the real production origin
 * (https://www.bullchargeratrace.com) rather than localhost. It defaults to the
 * production origin so even a plain `vite build` bakes correct absolute URLs.
 */

const RAW_SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) || "https://www.bullchargeratrace.com";

/** Production origin with any trailing slash trimmed, e.g. "https://www.bullchargeratrace.com". */
export const SITE_URL = RAW_SITE_URL.replace(/\/+$/, "");

/** Build an absolute URL for a site-relative path. `absoluteUrl("/race-day") → https://www.bullchargeratrace.com/race-day`. */
export function absoluteUrl(path = "/"): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${suffix}`;
}

// --- Brand / event facts (reused by JSON-LD builders + <title>s) ---
export const SITE_NAME = "Bursa Bull Charge × The Edge KL Rat Race 2026";
export const ORG_NAME = "Bursa Bull Charge × The Edge KL Rat Race";
export const ORG_CONTACT_EMAIL = "BBCKLRR@bizedge.com";
export const ORG_LOGO_URL = absoluteUrl("/BBCxTEKLRR2026_Logo-PNG.png");

/** Default social/AI share image — the 2026 landscape key visual. */
export const DEFAULT_OG_IMAGE = absoluteUrl("/key-visual-v2-landscape.jpeg");

/**
 * Search aliases for the event brand, emitted as schema.org `alternateName`.
 *
 * Why this exists: people search "BBC rat race", not the full 46-character brand
 * name. "BBC" is the long-standing abbreviation for Bursa Bull Charge and appears
 * in Bursa Malaysia's own materials (e.g. BBC2024_Brochure.pdf), but before this
 * the string "BBC" existed on the site only inside the contact email and image
 * filenames — neither of which a search engine reads as a brand token.
 *
 * Note the plain "x" variants: our titles use the multiplication sign "×" (U+00D7),
 * which nobody types into a search box.
 *
 * Caveat we cannot engineer around: a bare "BBC" query belongs to the British
 * Broadcasting Corporation. These aliases target "BBC rat race" / "BBC KL rat race",
 * where the modifier disambiguates.
 */
export const BRAND_ALTERNATE_NAMES = [
  "BBC Rat Race",
  "BBC KL Rat Race",
  "BBC x TEKLRR",
  "Bursa Bull Charge",
  "Bursa Bull Charge x The Edge KL Rat Race 2026",
  "Bull Charge x Rat Race",
  "KL Rat Race",
];

/**
 * Visually-hidden brand suffix appended inside every page's <h1>.
 *
 * Deliberately NOT a translated string: it is a brand name, identical in EN and MS
 * (the previous en/ms `home.hero.headingSr` values were byte-identical).
 *
 * Why it is needed: only the home page carried the brand in its heading. The other
 * indexable pages' H1s were "Backed By Corporate Malaysia", "Run With Purpose",
 * "Get In Touch", "Race Details" and "Accessibility statement" — none of which
 * contain the brand or even the words "Rat Race", so none of them could rank for a
 * branded query. Rendered via `sr-only` so nothing changes visually; this is the
 * same accessible pattern the home hero already used.
 */
export const BRAND_HEADING_SUFFIX =
  " — Bursa Bull Charge (BBC) × The Edge KL Rat Race 2026";

export const SITE_DESCRIPTION =
  "The Bursa Bull Charge × The Edge KL Rat Race 2026 is a corporate relay race by Bursa Malaysia and The Edge through Kuala Lumpur's financial district on 28 November 2026. Teams of four run for financial literacy and disability inclusion — Run as One. Run for All.";

/**
 * Append the event brand to a page label for a full document <title>.
 * `pageTitle("Race Day") → "Race Day | Bursa Bull Charge × The Edge KL Rat Race 2026"`.
 */
export function pageTitle(label: string): string {
  return `${label} | ${SITE_NAME}`;
}

// --- Event schedule + venue (schema.org/SportsEvent). Local time is Asia/Kuala_Lumpur (+08:00). ---
// Keep in step with the visible copy in i18n/locales/*/home.json → keyDetails.time
// and raceday.json → meetUp.time. It is an evening/night run: these previously said
// 06:30–13:00 while the page said 3 PM–10 PM, which would have put the wrong time in
// Google's event rich result.
export const EVENT_START_ISO = "2026-11-28T15:00:00+08:00"; // 3:00 PM, Sat 28 Nov 2026 KL
export const EVENT_END_ISO = "2026-11-28T22:00:00+08:00"; // 10:00 PM, Sat 28 Nov 2026 KL
export const EVENT_VENUE_NAME = "Bursa Malaysia, Exchange Square";
export const EVENT_STREET_ADDRESS = "Exchange Square, Bukit Kewangan";
export const EVENT_LOCALITY = "Kuala Lumpur";
export const EVENT_POSTAL_CODE = "50200";
export const EVENT_COUNTRY = "MY";

/**
 * Authoritative profiles for this event elsewhere on the web, emitted as
 * schema.org `sameAs`. This is how Google links the "Bursa Bull Charge" entity it
 * already knows about to THIS domain — the single strongest on-page signal we have
 * for a branded query.
 *
 * These are the official Bursa Bull Charge channels. If the 2026 joint edition
 * gets its own dedicated handles, replace them here — a `sameAs` pointing at a
 * profile the client does not control is worse than omitting the field.
 */
export const BRAND_SAME_AS = [
  "https://www.facebook.com/bursabullcharge/",
  "https://www.instagram.com/officialbursabullcharge/",
  "https://www.bursamalaysia.com/about_bursa/sustainability/yayasan-bursa-malaysia/bursa-bull-charge",
];
